import {
	Backdrop,
	CircularProgress,
	Stack,
	TablePagination,
	TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import React, {
	FunctionComponent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { HistoryTable } from '../../components/HistoryTable';
import { HistoryFilter } from '../../interfaces';
import { useHistoryService } from '../../services';
import { useTranslation } from '../../services';

const AdminHistory: FunctionComponent = () => {
	const { t } = useTranslation();
	const { meta, historyItems, getHistory } = useHistoryService();
	const [loading, setLoading] = useState(false);
	const [filters, setFilters] = useState<any>({});
	const [fromDate, setFromDate] = useState<Dayjs | null>(null);
	const [toDate, setToDate] = useState<Dayjs | null>(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const searchRef = useRef(null);

	useEffect(() => {
		(async () => {
			setLoading(true);
			await getHistory(filters);
			setLoading(false);
		})();
	}, [filters]);

	const handleSearch = useCallback(
		(value: string) => {
			if (value === '') {
				handlerFilters({ search: undefined });
			} else {
				handlerFilters({ search: value });
			}
		},
		[filters, setFilters]
	);

	const handlerFilters = useCallback(
		(val: HistoryFilter) => {
			const newFilters = { ...filters, ...val };
			Object.keys(newFilters).forEach(
				(key) => newFilters[key] === undefined && delete newFilters[key]
			);
			setFilters({ ...newFilters });
		},
		[filters, setFilters]
	);

	const handleFromDateChange = useCallback(
		(newValue: Dayjs | null) => {
			setFromDate(newValue);
			if (newValue) {
				handlerFilters({ dateFrom: newValue.toISOString() });
			} else {
				handlerFilters({ dateFrom: undefined });
			}
		},
		[setFromDate]
	);

	const handleTODateChange = useCallback(
		(newValue: Dayjs | null) => {
			setToDate(newValue);
			if (newValue) {
				handlerFilters({ dateTo: newValue.toISOString() });
			} else {
				handlerFilters({ dateTo: undefined });
			}
		},
		[setToDate]
	);

	const handleChangePage = useCallback(
		(event: unknown, newPage: number) => {
			setPage(newPage);
		},
		[setPage]
	);

	const handleChangeRowsPerPage = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setRowsPerPage(parseInt(event.target.value, 10));
			setPage(0);
		},
		[setRowsPerPage, setPage]
	);

	useEffect(() => {
		handlerFilters({ page: page + 1, limit: rowsPerPage });
	}, [page, rowsPerPage]);

	return (
		<>
			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={loading}
			>
				<CircularProgress color="inherit" sx={{ m: 1 }} />
				<Stack direction="row" justifyContent="center">
					{t('Please wait...')}
				</Stack>
			</Backdrop>

			<Stack spacing={2} margin={2}>
				<TextField
					label={t('Search')}
					inputRef={searchRef}
					onChange={(e) => handleSearch(e.target.value)}
					fullWidth
				/>
				<Stack direction="row" spacing={2}>
					<DatePicker
						label={t('Date from')}
						format={t('MM/DD/YYYY')}
						value={fromDate}
						onChange={handleFromDateChange}
					/>

					<DatePicker
						label={t('Date to')}
						format={t('MM/DD/YYYY')}
						value={toDate}
						onChange={handleTODateChange}
					/>
				</Stack>
			</Stack>
			<HistoryTable
				data={historyItems}
				filter={filters}
				onFilterChange={handlerFilters}
				hidden={loading}
			/>
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				labelRowsPerPage={t('Rows per page')}
				count={meta.totalItems || historyItems.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</>
	);
};

export default AdminHistory;
